/**
 * Which of a business's industry desks each person opens, and Books (vsidebar-b, 4 Nov 2026).
 *
 * Founder, 3 Nov 2026: one tick per vertical the business has switched on, plus Books, per
 * person. An owner or admin always opens everything. A person with no row opens everything
 * their role allows, so nothing changes until someone ticks. The server is the authority:
 * every desk read asks `can_open_vertical` first (migration 20261104035605) and refuses with
 * the reason. This file is the shell's half: it hides what the server would refuse, so a
 * person never presses a row that only leads to a refusal.
 *
 * React-free and supabase-free on purpose: AppLayout, the sidebar and the tests read it.
 */
import type { NavZone } from '@/shared/components/sidebarNav';

export type VerticalKey =
    | 'health' | 'travel' | 'stay' | 'campus' | 'fleet' | 'jewel'
    | 'mandi' | 'service' | 'practice' | 'build' | 'me' | 'studio';
export type AccessKey = VerticalKey | 'books';

export interface VerticalDef {
    key: VerticalKey;
    /** The name a refusal and the Access grid use: "Karr Stay". */
    label: string;
    /** The short name of the grid's column: "Stay". */
    short: string;
    /** Where its pages live. A path is the vertical's when it is the prefix or under it. */
    prefixes: readonly string[];
    /** The modules (modules.ts) that switch it on: any one of them on = the business has it. */
    modules: readonly string[];
}

/** Every vertical, in the order the Access grid shows its columns. Keys match the SQL's CHECK. */
export const VERTICALS: readonly VerticalDef[] = [
    { key: 'stay', label: 'Karr Stay', short: 'Stay', prefixes: ['/stay'], modules: ['stay'] },
    { key: 'campus', label: 'Karr Campus', short: 'Campus', prefixes: ['/campus'], modules: ['campus'] },
    { key: 'fleet', label: 'Karr Fleet', short: 'Fleet', prefixes: ['/fleet'], modules: ['fleet'] },
    { key: 'jewel', label: 'Karr Jewel', short: 'Jewel', prefixes: ['/jewel'], modules: ['jewel'] },
    { key: 'mandi', label: 'Karr Mandi', short: 'Mandi', prefixes: ['/mandi'], modules: ['mandi'] },
    { key: 'service', label: 'Karr Service', short: 'Service', prefixes: ['/service'], modules: ['service'] },
    { key: 'practice', label: 'Karr Practice', short: 'Practice', prefixes: ['/practice/matters'], modules: ['practice-matters'] },
    { key: 'build', label: 'Karr Build', short: 'Build', prefixes: ['/build'], modules: ['build'] },
    { key: 'me', label: 'Karr Me', short: 'Me', prefixes: ['/me'], modules: ['me'] },
    { key: 'studio', label: 'Karr Studio', short: 'Studio', prefixes: ['/studio'], modules: ['studio'] },
    { key: 'health', label: 'Karr Health', short: 'Health', prefixes: ['/health', '/hms'], modules: ['hms', 'lab'] },
    { key: 'travel', label: 'Karr Travel', short: 'Travel', prefixes: ['/travel'], modules: ['travel'] },
];

export const ACCESS_KEYS: readonly AccessKey[] = ['books', ...VERTICALS.map((v) => v.key)];

/** What the shell read for this person on this business. */
export interface VerticalAccess {
    /** True: everything their role allows (an owner, an admin, or nobody has ticked for them). */
    all: boolean;
    /** When not `all`: the ticks, Books included as 'books'. */
    open: readonly AccessKey[];
}

/** Everything their role allows: today's behaviour, and the answer before the paste. */
export const OPEN_ALL: VerticalAccess = { all: true, open: [] };

/** Reads the server's answer; anything malformed is today's behaviour, never a lock-out. */
export function accessFrom(raw: unknown): VerticalAccess {
    const r = (raw ?? {}) as { all?: unknown; open?: unknown };
    if (r.all !== false) return OPEN_ALL;
    const open = Array.isArray(r.open) ? r.open.filter((k): k is AccessKey => ACCESS_KEYS.includes(k as AccessKey)) : [];
    return { all: false, open };
}

/** May this person open it? Not read yet (null) = yes: the server still refuses what it must. */
export function canOpen(access: VerticalAccess | null | undefined, key: AccessKey): boolean {
    if (!access || access.all) return true;
    return access.open.includes(key);
}

const under = (path: string, prefix: string) => path === prefix || path.startsWith(`${prefix}/`);

/** The vertical a path belongs to, or null for a books page (or anything else). */
export function verticalOfPath(href: string): VerticalDef | null {
    const path = href.split(/[?#]/)[0];
    return VERTICALS.find((v) => v.prefixes.some((p) => under(path, p))) ?? null;
}

export function verticalByKey(key: string): VerticalDef | null {
    return VERTICALS.find((v) => v.key === key) ?? null;
}

/**
 * Places a person without Books still opens: their own settings and profile, notifications,
 * help, and Access (where the Email button to ask an owner lives). Everything else that is
 * not a vertical is the books.
 */
const NOT_BOOKS = ['/settings', '/profile', '/notifications', '/help', '/collaboration', '/select-org', '/onboarding', '/welcome', '/account'];

/** What a path needs: a vertical's key, 'books', or null when anyone on the business may open it. */
export function accessKeyOfPath(href: string): AccessKey | null {
    const v = verticalOfPath(href);
    if (v) return v.key;
    const path = href.split(/[?#]/)[0];
    if (NOT_BOOKS.some((p) => under(path, p))) return null;
    return 'books';
}

/** Whether a page may be drawn for this person; when not, which tick it needs. */
export function pageAccess(access: VerticalAccess | null | undefined, path: string): { ok: true } | { ok: false; key: AccessKey } {
    const key = accessKeyOfPath(path);
    if (!key || canOpen(access, key)) return { ok: true };
    return { ok: false, key };
}

/** The words the page uses when it refuses, the same as the server's. */
export function refusalText(key: AccessKey, orgName: string | null | undefined): string {
    const label = key === 'books' ? 'Books' : (verticalByKey(key)?.label ?? 'This desk');
    return `${label} is not open to you at ${orgName || 'this business'}. An owner or admin can tick it for you on the Access page.`;
}

/** The first place this person may open, for the refusal's way on: Books, else their first desk. */
export function firstOpenPlace(access: VerticalAccess | null | undefined): { key: AccessKey; label: string; href: string } | null {
    if (canOpen(access, 'books')) return { key: 'books', label: 'Books', href: '/dashboard' };
    for (const v of VERTICALS) {
        if (canOpen(access, v.key)) return { key: v.key, label: v.label, href: v.prefixes[0] };
    }
    return null;
}

/**
 * The books' sidebar as this person may use it: a row for a vertical they cannot open is left
 * out, and without Books every books row is left out (the vertical rows and the places anyone
 * may open stay). A group or zone left with no row goes too.
 */
export function zonesForAccess(zones: NavZone[], access: VerticalAccess | null | undefined): NavZone[] {
    if (!access || access.all) return zones;
    return zones
        .map((z) => ({
            ...z,
            groups: z.groups
                .map((g) => ({
                    ...g,
                    items: g.items.filter((it) => {
                        const key = accessKeyOfPath(it.href);
                        return key === null || canOpen(access, key);
                    }),
                }))
                .filter((g) => g.items.length > 0),
        }))
        .filter((z) => z.groups.length > 0);
}

/** The verticals this business has switched on, for the Access grid's columns. */
export function verticalsSwitchedOn(isActive: (moduleId: string) => boolean): VerticalDef[] {
    return VERTICALS.filter((v) => v.modules.some((m) => isActive(m)));
}
